import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import BackgroundVideo from "@/components/BackgroundVideo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";

export default function Signup() {
  const { setUser, fetchTeam } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", team_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      if (data?.team_id) await fetchTeam();
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const signupGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ backgroundColor: "var(--ink-1)" }}>
      <div className="flex items-center justify-center p-6 md:p-12 order-2 md:order-1" style={{ backgroundColor: "var(--ink-1)" }}>
        <form onSubmit={submit} className="w-full max-w-md" data-testid="signup-form">
          <div className="md:hidden mb-10"><Logo size="sm" variant="lockup" linkTo="/" dark /></div>
          <h1 className="font-display font-black text-4xl tracking-tighter text-white">Create your team</h1>
          <p className="text-white/50 mt-2 text-sm">
            Already have an account? <Link to="/login" className="font-semibold" style={{ color: "var(--brand-secondary)" }}>Sign in</Link>
          </p>
          <div className="mt-4 rounded-lg border border-cyan-400/20 bg-cyan-400/5 px-4 py-3 text-xs text-white/60" data-testid="signup-athlete-notice">
            <span className="font-bold text-cyan-300">Athletes:</span> don't create a coach account here — use the invite link your coach sent you to join your team's roster or Fusion Hit-A-Thon.
          </div>

          <div className="space-y-4 mt-8">
            <div>
              <Label htmlFor="name" className="text-white/70">Your name</Label>
              <Input id="name" data-testid="signup-name-input" required value={form.name} onChange={upd("name")} placeholder="Coach Rodriguez"
                className="mt-2 h-12 bg-[var(--ink-2)] border-[var(--ink-border)] text-white placeholder:text-white/30" />
            </div>
            <div>
              <Label htmlFor="team_name" className="text-white/70">Team / Organization</Label>
              <Input id="team_name" data-testid="signup-team-input" required value={form.team_name} onChange={upd("team_name")} placeholder="Eagles Baseball Academy"
                className="mt-2 h-12 bg-[var(--ink-2)] border-[var(--ink-border)] text-white placeholder:text-white/30" />
            </div>
            <div>
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <Input id="email" data-testid="signup-email-input" type="email" required value={form.email} onChange={upd("email")} placeholder="coach@yourteam.com"
                className="mt-2 h-12 bg-[var(--ink-2)] border-[var(--ink-border)] text-white placeholder:text-white/30" />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <Input id="password" data-testid="signup-password-input" type="password" minLength={6} required value={form.password} onChange={upd("password")} placeholder="At least 6 characters"
                className="mt-2 h-12 bg-[var(--ink-2)] border-[var(--ink-border)] text-white placeholder:text-white/30" />
            </div>
            {error && <div className="text-sm text-red-400" data-testid="signup-error">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="signup-submit-button"
              className="w-full h-12 rounded-md text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }}>
              {loading ? "Creating…" : <>Create team <ArrowRight className="ml-1.5 h-4 w-4" /></>}
            </Button>
          </div>

          <div className="flex items-center gap-3 my-6 text-xs text-white/30 uppercase tracking-[0.25em]">
            <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
          </div>

          <Button type="button" variant="outline" onClick={signupGoogle} data-testid="signup-google-button"
            className="w-full h-12 rounded-md border-2 border-white/15 bg-white/5 text-white hover:bg-white/10">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/><path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/><path fill="#FBBC04" d="M11.69 28.18A13.5 13.5 0 0 1 10.97 24c0-1.45.25-2.85.72-4.18v-5.7H4.34A21.98 21.98 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/><path fill="#FBBC04" d="M11.69 28.18A13.5 13.5 0 0 1 10.97 24c0-1.45.25-2.85.72-4.18v-5.7H4.34A21.98 21.98 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/><path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z"/></svg>
            Continue with Google
          </Button>
        </form>
      </div>

      <div className="hidden md:flex flex-col justify-between p-12 text-white relative overflow-hidden order-1 md:order-2">
        <BackgroundVideo />
        <Logo size="md" variant="lockup" linkTo="/" className="relative z-10" dark />
        <div className="relative z-10 max-w-md">
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>
            Multi-tenant
          </div>
          <h2 className="font-display font-black text-5xl tracking-tighter mt-3 leading-[0.95]">
            Your tenant.<br />Your branding.<br />Your protocols.
          </h2>
          <p className="text-white/70 mt-6 leading-relaxed">
            Configure your brand hex codes, toggle which biomechanical metrics you track, paste FlightScope or Rapsodo exports, and onboard players in under a minute.
          </p>
        </div>
        <div className="relative z-10 text-[11px] text-white/40 tracking-[0.25em] uppercase font-semibold">
          Free to start · No credit card
        </div>
      </div>
    </div>
  );
}
